import React, { useState, useEffect } from 'react';
import { Table, Checkbox, Button, message, Typography, Spin } from 'antd';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../data/base';
import { secureGetItem } from '../../data/utils';
import { LogVariantService } from '../services/Utils/LogVariantService';
import { showSuccessNotification, showErrorNotification } from '../../utils/notifications';
import ButtonState from '../../components/Components/Buttons/ButtonState';
import HoverImageZoom from '../../components/Components/Images/HoverImageZoom';
import CustomTooltip from '../../components/Components/Modals/CustomTooltip';
import FullScreenModal from '../../components/Components/Modals/FullScreenModal';
import SmallModal from '../../components/Components/Modals/SmallModal';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const Publish3DAssetsModal = ({ visible, onClose, brandId }) => {
    const [variants, setVariants] = useState({});
    const [loading, setLoading] = useState(false);
    const [selectedVariants, setSelectedVariants] = useState([]);
    const [publishing, setPublishing] = useState(false);
    const [expandedProducts, setExpandedProducts] = useState([]);
    const [processedImages, setProcessedImages] = useState({});
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // Funzione per rimuovere lo sfondo bianco dalle immagini
    const removeWhiteBackground = (imgUrl, id) => {
        if (processedImages[id]) return processedImages[id];

        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext("2d", { willReadFrequently: true });

                if (!ctx) {
                    resolve(imgUrl);
                    return;
                }

                ctx.drawImage(img, 0, 0);

                try {
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const data = imageData.data;

                    for (let i = 0; i < data.length; i += 4) {
                        const red = data[i];
                        const green = data[i + 1];
                        const blue = data[i + 2];

                        if (red > 240 && green > 240 && blue > 240) {
                            data[i + 3] = 0; // Rendi trasparente
                        }
                    }

                    ctx.putImageData(imageData, 0, 0);
                    const processedImg = canvas.toDataURL("image/png");
                    setProcessedImages(prev => ({ ...prev, [id]: processedImg }));
                    resolve(processedImg);
                } catch (error) {
                    console.error("Errore nel processare l'immagine:", error);
                    resolve(imgUrl);
                }
            };

            img.onerror = () => {
                console.error("Errore nel caricamento dell'immagine");
                resolve(imgUrl);
            };

            img.src = imgUrl;
        });
    };

    // Carica tutte le varianti con stato "In Publication" per il brand corrente e chiama la funzione fetchInPublicationVariants
    useEffect(() => {
        if (visible && brandId) {
            fetchInPublicationVariants();
        }
    }, [visible, brandId]);

    // Funzione per recuperare le varianti con stato "In Pubblicazione" per il brand corrente
    const fetchInPublicationVariants = async () => {
        setLoading(true);
        try {
            // console.log("Inizio recupero varianti per brandId:", brandId);

            // Estrai l'ID del brand pulito
            const cleanBrandId = brandId.replace("id=", "");
            // console.log("Brand ID pulito:", cleanBrandId);

            // Prima recupera tutti i prodotti del brand
            const productsQuery = query(
                collection(db, "Products"),
                where("mainBrandRef", "==", cleanBrandId)
            );
            const productsSnapshot = await getDocs(productsQuery);
            const productIds = productsSnapshot.docs.map(doc => doc.id);

            // console.log("Prodotti trovati:", productIds.length, productIds);

            if (productIds.length === 0) {
                console.log("Nessun prodotto trovato per questo brand");
                setVariants({});
                setLoading(false);
                return;
            }

            // Dividi i productIds in batch di 30 (limite Firebase per IN)
            const batchSize = 30;
            const productBatches = [];
            for (let i = 0; i < productIds.length; i += batchSize) {
                productBatches.push(productIds.slice(i, i + batchSize));
            }

            // console.log(`Diviso in ${productBatches.length} batch:`, productBatches.map(batch => batch.length));

            // Esegui query per ogni batch e unisci i risultati
            const allVariantsSnapshots = await Promise.all(
                productBatches.map(async (batch) => {
                    const variantsQuery = query(
                        collection(db, "Variants"),
                        where("productRef", "in", batch),
                        where("status", "==", "In Pubblicazione")
                    );
                    return await getDocs(variantsQuery);
                })
            );

            // Unisci tutti i documenti
            const allVariantDocs = allVariantsSnapshots.flatMap(snapshot => snapshot.docs);
            // console.log("Varianti 'In Pubblicazione' trovate:", allVariantDocs.length);

            const variantsData = await Promise.all(
                allVariantDocs.map(async (variantDoc) => {
                    const variantData = { id: variantDoc.id, ...variantDoc.data() };
                    // console.log("Processando variante:", variantData.id, variantData.glassesName);

                    // Recupera il nome del prodotto
                    try {
                        const productDoc = await getDoc(doc(db, "Products", variantData.productRef));
                        if (productDoc.exists()) {
                            variantData.productName = productDoc.data().modelName;
                        } else {
                            // console.log("Prodotto non trovato per variante:", variantData.productRef);
                            variantData.productName = "Prodotto non trovato";
                        }
                    } catch (error) {
                        console.error("Errore nel recupero del prodotto:", error);
                        variantData.productName = "Nome non disponibile";
                    }

                    return variantData;
                })
            );

            // console.log("Varianti processate:", variantsData.length);

            // Raggruppa le varianti per prodotto e recupera le informazioni del prodotto
            const groupedVariants = {};

            for (const variant of variantsData) {
                const productKey = variant.productRef;
                if (!groupedVariants[productKey]) {
                    // Recupera le informazioni complete del prodotto
                    try {
                        const productDoc = await getDoc(doc(db, "Products", productKey));
                        if (productDoc.exists()) {
                            const productData = productDoc.data();
                            groupedVariants[productKey] = {
                                id: productKey,
                                productName: productData.modelName,
                                imgUrl: productData.imgUrl,
                                sku: productData.sku,
                                variants: []
                            };
                        }
                    } catch (error) {
                        console.error("Errore nel recupero del prodotto completo:", error);
                        groupedVariants[productKey] = {
                            id: productKey,
                            productName: variant.productName,
                            imgUrl: null,
                            sku: null,
                            variants: []
                        };
                    }
                }
                groupedVariants[productKey].variants.push(variant);
            }

            // console.log("Varianti raggruppate per prodotto:", Object.keys(groupedVariants).length);
            setVariants(groupedVariants);
        } catch (error) {
            console.error("Errore nel recupero delle varianti:", error);
            message.error("Error retrieving variants: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Funzione per selezionare una variante
    const handleSelectVariant = (variantId, checked) => {
        if (checked) {
            setSelectedVariants(prev => [...prev, variantId]);
        } else {
            setSelectedVariants(prev => prev.filter(id => id !== variantId));
        }
    };

    // Funzione per selezionare tutte le varianti
    const handleSelectAll = (checked) => {
        if (checked) {
            const allVariantIds = Object.values(variants).flatMap(product =>
                product.variants.map(variant => variant.id)
            );
            setSelectedVariants(allVariantIds);
        } else {
            setSelectedVariants([]);
        }
    };

    // Funzione per selezionare un prodotto
    const handleSelectProduct = (productId, checked) => {
        const product = variants[productId];
        if (!product) return;

        const productVariantIds = product.variants.map(variant => variant.id);

        if (checked) {
            setSelectedVariants(prev => [...new Set([...prev, ...productVariantIds])]);
        } else {
            setSelectedVariants(prev => prev.filter(id => !productVariantIds.includes(id)));
        }
    };

    // Funzione che apre la modale di conferma prima della pubblicazione
    const handlePublishNow = () => {
        if (selectedVariants.length === 0) {
            message.warning("Select at least one variant to publish");
            return;
        }
        setShowConfirmModal(true);
    };

    // Funzione per pubblicare le varianti selezionate e aggiornare lo stato delle varianti dopo la pubblicazione
    const handleConfirmPublish = async () => {
        setShowConfirmModal(false);
        setPublishing(true);
        const userEmail = secureGetItem("email");
        const userRole = secureGetItem("role");
        const publishTimestamp = Timestamp.now();

        try {
            // Raccogli tutti i productRef unici dalle varianti selezionate
            const uniqueProductRefs = new Set();

            // Aggiorna tutte le varianti selezionate
            await Promise.all(selectedVariants.map(async (variantId) => {
                const variantRef = doc(db, "Variants", variantId);

                // Prima recupera la variante per ottenere il productRef
                const variantDoc = await getDoc(variantRef);
                if (variantDoc.exists()) {
                    const variantData = variantDoc.data();
                    uniqueProductRefs.add(variantData.productRef);
                }

                // Aggiorna lo stato e aggiunge il timestamp di pubblicazione nella collezione 'Variants'
                await updateDoc(variantRef, {
                    status: "Pubblicato",
                    publishedInARSLibrary: publishTimestamp,
                    lastUpdate: publishTimestamp
                });

                // Aggiunge il log di validazione con tipo "Published in ARSLibrary" nella collezione 'LogVariants'
                await LogVariantService.logValidateVariant(
                    variantId,
                    "Pubblicato",
                    {
                        account: userEmail,
                        role: userRole,
                        time: publishTimestamp,
                        modeling_note: "Published in ARS Library"
                    }
                );
            }));

            // Aggiorna lo stato di tutti i prodotti unici a "Pubblicato" e incrementa availableVariants
            await Promise.all(Array.from(uniqueProductRefs).map(async (productRef) => {
                const productDocRef = doc(db, "Products", productRef);

                // Recupera il prodotto per ottenere il valore corrente di availableVariants
                const productDoc = await getDoc(productDocRef);
                if (productDoc.exists()) {
                    const productData = productDoc.data();

                    // Trova tutte le varianti di questo prodotto che sono state pubblicate
                    const variantsPublishedForThisProduct = [];
                    selectedVariants.forEach(variantId => {
                        // Trova la variante nel nostro stato locale per verificare il productRef
                        for (const product of Object.values(variants)) {
                            const variant = product.variants.find(v => v.id === variantId);
                            if (variant && variant.productRef === productRef) {
                                variantsPublishedForThisProduct.push(variant);
                            }
                        }
                    });

                    const currentAvailableVariants = productData.availableVariants || 0;

                    // Prepara le mappe delle varianti per variants_map
                    const variantMaps = variantsPublishedForThisProduct.map(variant => ({
                        eanCode: variant.eanCode || "",
                        id: variant.id,
                        skuCode: variant.skuModel || "",
                        upcCode: variant.upcCode || "",
                        variantName: variant.glassesName || "",
                        publishedInARSLibrary: publishTimestamp
                    }));

                    // Ottieni l'array esistente o creane uno nuovo
                    const currentVariantsMap = productData.variants_map || [];

                    // Gestisci l'aggiunta/aggiornamento delle mappe delle varianti
                    let updatedVariantsMap = [...currentVariantsMap];

                    variantMaps.forEach(variantMap => {
                        const existingVariantIndex = updatedVariantsMap.findIndex(v => v.id === variantMap.id);

                        if (existingVariantIndex !== -1) {
                            // Se esiste, aggiorna la mappa esistente
                            updatedVariantsMap[existingVariantIndex] = variantMap;
                        } else {
                            // Se non esiste, aggiungi la nuova mappa
                            updatedVariantsMap.push(variantMap);
                        }
                    });

                    await updateDoc(productDocRef, {
                        status: "Pubblicato",
                        lastUpdate: publishTimestamp,
                        availableVariants: currentAvailableVariants + variantsPublishedForThisProduct.length,
                        lastPublishedInARSLibrary: publishTimestamp,
                        variants_map: updatedVariantsMap
                    });

                    console.log(`Product ${productRef} status updated to "Pubblicato" and availableVariants incremented from ${currentAvailableVariants} to ${currentAvailableVariants + variantsPublishedForThisProduct.length}`);

                } else {
                    console.error(`Product ${productRef} not found`);
                    // Fallback: aggiorna solo status e lastUpdate se il prodotto non viene trovato
                    await updateDoc(productDocRef, {
                        status: "Pubblicato",
                        lastUpdate: publishTimestamp,
                        lastPublishedInARSLibrary: publishTimestamp
                    });
                }
            }));

            showSuccessNotification(`${selectedVariants.length} variants published successfully!`);
            setSelectedVariants([]);
            fetchInPublicationVariants(); // Ricarica la lista
            onClose();
        } catch (error) {
            console.error("Errore nella pubblicazione:", error);
            showErrorNotification("Error during variant publication");
        } finally {
            setPublishing(false);
        }
    };

    // Calcola il totale delle varianti
    const totalVariants = Object.values(variants).reduce((total, product) => total + product.variants.length, 0);

    // Crea la struttura dati per la tabella principale (solo prodotti)
    const productsData = Object.values(variants).map(product => ({
        ...product,
        variantCount: product.variants.length
    }));

    // Colonne per la tabella principale dei prodotti
    const productColumns = [
        {
            title: (
                <Checkbox
                    checked={selectedVariants.length === totalVariants && totalVariants > 0}
                    indeterminate={selectedVariants.length > 0 && selectedVariants.length < totalVariants}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                />
            ),
            dataIndex: 'select',
            key: 'select',
            render: (_, product) => {
                const productVariantIds = product.variants.map(v => v.id);
                const isAllSelected = productVariantIds.every(id => selectedVariants.includes(id));
                const isPartialSelected = productVariantIds.some(id => selectedVariants.includes(id));

                return (
                    <Checkbox
                        checked={isAllSelected}
                        indeterminate={isPartialSelected && !isAllSelected}
                        onChange={(e) => handleSelectProduct(product.id, e.target.checked)}
                    />
                );
            },
            width: '4%',
            align: 'center',
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>Image</span>
                    <CustomTooltip title="Image of the product">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'imgUrl',
            key: 'imgUrl',
            render: (imgUrl, record) => {
                const imgSrc = processedImages[record.id] || imgUrl;

                if (!processedImages[record.id] && imgUrl) {
                    removeWhiteBackground(imgUrl, record.id);
                }

                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <HoverImageZoom
                            src={imgSrc}
                            alt="Product"
                            style={{ width: 'clamp(2.5rem, 2rem + 2vw, 3.125rem)', objectFit: 'contain' }}
                        />
                    </div>
                );
            },
            width: '12%',
            align: 'center',
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>Product Name</span>
                    <CustomTooltip title="Name of the product">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'productName',
            key: 'productName',
            width: '59%',
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <span>Selected Variants</span>
                    <CustomTooltip title="Number of selected variants for this product">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            key: 'selectedCount',
            render: (_, product) => {
                const productVariantIds = product.variants.map(v => v.id);
                const selectedCount = productVariantIds.filter(id => selectedVariants.includes(id)).length;

                return (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        fontWeight: 'bold',
                        color: selectedCount > 0 ? '#929ACC' : '#666'
                    }}>
                        {selectedCount} / {product.variantCount}
                    </div>
                );
            },
            align: 'right',
            width: '25%',
        },
    ];

    // Colonne per la tabella espansa delle varianti
    const variantColumns = [
        {
            title: '',
            dataIndex: 'select',
            key: 'select',
            render: (_, variant) => (
                <Checkbox
                    checked={selectedVariants.includes(variant.id)}
                    onChange={(e) => handleSelectVariant(variant.id, e.target.checked)}
                />
            ),
            width: '5%',
            align: 'center',
        },
        {
            title: 'Image',
            dataIndex: 'poster',
            key: 'poster',
            render: (imgUrl, variant) => {
                const imgSrc = processedImages[variant.id] || imgUrl;

                if (!processedImages[variant.id] && imgUrl) {
                    removeWhiteBackground(imgUrl, variant.id);
                }

                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <HoverImageZoom
                            src={imgSrc}
                            alt="Variant"
                            style={{ width: 'clamp(2.5rem, 2rem + 2vw, 3.125rem)', objectFit: 'contain' }}
                        />
                    </div>
                );
            },
            width: '8%',
            align: 'center',
        },
        {
            title: 'Variant Name',
            dataIndex: 'glassesName',
            key: 'glassesName',
            width: '20%',
        },
        {
            title: 'SKU',
            dataIndex: 'skuModel',
            key: 'skuModel',
            width: '15%',
        },
        {
            title: 'Frame Color',
            dataIndex: 'frameColor',
            key: 'frameColor',
            width: '12%',
        },
        {
            title: 'Lenses Color',
            dataIndex: 'lensesColor',
            key: 'lensesColor',
            render: (text) => text === "0" || !text ? "TRANSPARENT" : text,
            width: '12%',
        },
        {
            title: 'Size',
            dataIndex: 'size',
            key: 'size',
            render: (size) => size?.size || '',
            width: '8%',
            align: 'center',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: () => (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ButtonState
                        state="In Publication"
                        text="In Publication"
                        style={{ width: '100%', maxWidth: '120px' }}
                    />
                </div>
            ),
            width: '20%',
            align: 'right',
        },
    ];

    // Crea il footer personalizzato
    const customFooter = (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Text strong style={{ fontSize: '16px', color: '#929ACC' }}>
                {selectedVariants.length} of {totalVariants} variants selected
            </Text>
            <div style={{ display: 'flex', gap: '12px' }}>
                <Button
                    onClick={onClose}
                    disabled={publishing}
                    size="large"
                >
                    Cancel
                </Button>
                <Button
                    type="primary"
                    loading={publishing}
                    onClick={handlePublishNow}
                    disabled={selectedVariants.length === 0}
                    size="large"
                    style={{
                        backgroundColor: selectedVariants.length > 0 ? '#52c41a' : undefined,
                        borderColor: selectedVariants.length > 0 ? '#52c41a' : undefined,
                    }}
                >
                    Publish Now ({selectedVariants.length})
                </Button>
            </div>
        </div>
    );

    return (
        <FullScreenModal
            visible={visible}
            onClose={onClose}
            title="Publish 3D Assets"
            customLeftButton={customFooter}
            closeButtonPosition="none"
        >
            <div style={{ marginBottom: '16px' }}>
                <Text type="secondary">
                    Select variants that you want to publish to the ARS library.
                    Expand products to view and select individual variants.
                </Text>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: '16px' }}>
                        <Text>Loading variants...</Text>
                    </div>
                </div>
            ) : (
                <Table
                    columns={productColumns}
                    dataSource={productsData}
                    rowKey="id"
                    pagination={{
                        defaultPageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 25, 50, 100],
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} products (${totalVariants} variants total)`
                    }}
                    size="small"
                    scroll={{ x: 1000 }}
                    locale={{
                        emptyText: "No products with 'In Publication' variants found"
                    }}
                    expandable={{
                        expandedRowRender: (product) => (
                            <div style={{ padding: "clamp(0.5rem, 0.4rem + 0.5vw, 0.625rem)" }}>
                                <Typography.Text strong style={{ display: 'block', marginBottom: 'clamp(0.5rem, 0.4rem + 0.5vw, 0.75rem)', fontSize: "clamp(0.875rem, 0.8rem + 0.25vw, 1rem)" }}>
                                    Available variants: {product.variants.length}
                                </Typography.Text>
                                <Table
                                    columns={variantColumns}
                                    dataSource={product.variants}
                                    rowKey="id"
                                    pagination={{
                                        defaultPageSize: 10,
                                        showSizeChanger: true,
                                        pageSizeOptions: [10, 15, 20, 25, 30],
                                    }}
                                    size="small"
                                    showSorterTooltip={false}
                                    scroll={{ x: 980 }}
                                />
                            </div>
                        ),
                        expandedRowKeys: expandedProducts,
                        onExpand: (expanded, record) => {
                            setExpandedProducts(expanded
                                ? [...expandedProducts, record.id]
                                : expandedProducts.filter(key => key !== record.id)
                            );
                        },
                        expandIconColumnIndex: 1, // Sposta l'icona di espansione alla colonna 1 (dopo la checkbox)
                    }}
                />
            )}

            {/* Confirmation modal for publishing variants */}
            <SmallModal
                visible={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                title=""
                onConfirm={handleConfirmPublish}
                confirmText="Publish"
                cancelText="Cancel"
                confirmLoading={publishing}
            >
                <div style={{ textAlign: 'center' }}>
                    <Text strong style={{ color: '#000000' }}>
                        Are you sure you want to publish {selectedVariants.length} variant{selectedVariants.length !== 1 ? 's' : ''} to the ARS library?
                    </Text>
                    <br />
                    <br />
                    <Text style={{ color: '#666666' }}>
                        Click Yes if you want to proceed.
                    </Text>
                    <br />
                    <Text style={{ color: '#666666' }}>
                        Once confirmed, they will be added to the ARShades Library and will be immediately available for import into your catalogues.
                    </Text>
                </div>
            </SmallModal>
        </FullScreenModal>
    );
};

export default Publish3DAssetsModal; 